import { faClipboard } from "@fortawesome/free-solid-svg-icons";
import { faCog, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FavoriteButton from "components/session/FavoriteButton";
import UnitSelectionModal from "components/UnitSelectionModal";
import _ from "lodash";
import memoize from "memoize-one";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { sticky } from "tippy.js";
import {
    archiveInspectionSession,
    getDeviceGroupInfo,
    getFavouriteCategories,
    routeSelected,
    setDisplayExtraLayers,
    targetResource,
    toggleInspectionAnnotationMode,
} from "../../../redux/actions/index";
import FindLocationPopup from "../../map/FindLocationPopup";
import MetadataFeed from "../../route/MetadataFeed";
import ELRMileAndChain from "../../util/ELRMileAndChain";
import { convertSpeed, parseRoutePositionData } from "../../util/Geometry";
import { LazyTippy } from "../../util/LazyTooltips";
import { getAbsoluteTimestamp } from "../../util/PlaylistUtils";
import RailInspectSourceModal from "./RailInspectSourceModal";

import { convertToTimezone } from "components/util/TimezoneUtils";
import { routeGeomSelector } from "redux/selectors";
import ArchiveInspectionSessionModal from "./ArchiveInspectionSessionModal";
import RestoreArchivedInspectionSessionModal from "./RestoreArchivedInspectionSessionModal";
const selectedRailImageIndexSelector = (state) => state.railInspection.selectedRailInspectionImage.index;
const elrUnitsSelector = (state) => state.userDetails.userConfig.elr_units;
const userDetailsSelector = (state) => state.userDetails;
const dashboardsSelector = (state) => state.dashboards;
const viewsSelector = (state) => state.views;
const sessionSelector = (state) => _.get(state.sessions, [state.playlist.data.routeID], []);
const secondarySessionDataSelector = (state) => state.railInspection.secondarySessionData;

const timestampSelector = (state) => {
    const routeID = state.playlist.data.routeID;
    const isVideo = state.playlist.position.isVideo;
    const sourceIndex = state.playlist.position.sourceIndex;
    const playlist = isVideo ? _.get(state.playlist.data, ["video", sourceIndex], []) : state.playlist.data.image;
    const index = state.playlist.position.currentIndex;
    const offset = state.playlist.position.currentTimeOffset || 0;
    return getAbsoluteTimestamp(routeID, playlist, index, isVideo, offset);
};
const railDataObjectSelector = (state) => state.railInspection.railInspectionImages.data;
const railDataLoadedSelector = (state) => state.railInspection.railInspectionImages.loadingInfo.loaded;

const videolessTimestampSelector = (state) => {
    const currentIndex = selectedRailImageIndexSelector(state);
    const railImages = state.railInspection.railInspectionImages.data;

    let timestamp = 0;
    if (railImages[currentIndex]) {
        const currentImage = railImages[currentIndex];
        timestamp = currentImage.timestamp;
    }
    return timestamp;
};
const userConfigSelector = (state) => state.userDetails.userConfig;
const speedAndBearingSelector = (state) => _.get(state.routeMetadata, ["SPEED_AND_BEARING", state.playlist.data.routeID], []);
const tachoDataSelector = (state) => _.get(state.routeMetadata, ["TACHO_DATA", state.playlist.data.routeID], []);
const routeSystemIDSelector = (state) => state.playlist.data.system_id;
const foundRouteCoordinatesSelector = (state) => state.locationSearch.results;
const coordinatesSelector = (state) => state.playlist.position.coords;
const routePositionSelector = (state) => state.playlist.position.routePosition;

const RailInspectHeader = ({
    match,
    toggleRestoreArchivedInspectionSessionModal,
    restoreArchivedInspectionSessionModalVisible,
    toggleArchiveInspectionSessionModal,
    archiveInspectionSessionModalVisible,
    videoless,
    toggleFindModalVisibility,
    findModalVisible,
    setFavouritePopoverOpen,
    setCurrentHeaderHeight,
    onExitRailInspection,
    labelsRef,
}) => {
    const [sourceModalOpen, setSourceModalOpen] = useState(false);
    const railInspectionHeader = useRef();

    const history = useHistory();
    const dispatch = useDispatch();

    const selectedRailImageIndex = useSelector(selectedRailImageIndexSelector);

    const elrUnits = useSelector(elrUnitsSelector);
    const userDetails = useSelector(userDetailsSelector);
    const dashboards = useSelector(dashboardsSelector);
    const views = useSelector(viewsSelector);
    const session = useSelector(sessionSelector);
    const sessionID = session.id || parseInt(match.params.sessionID);

    const timestamp = useSelector(timestampSelector);
    const routeLocationData = useSelector(routeGeomSelector);
    const railDataObject = useSelector(railDataObjectSelector);
    const railDataLoaded = useSelector(railDataLoadedSelector);
    const videolessTimestamp = useSelector(videolessTimestampSelector);
    const routeSystemID = useSelector(routeSystemIDSelector);
    const foundRouteCoordinates = useSelector(foundRouteCoordinatesSelector);
    const currentCoordinates = useSelector(coordinatesSelector);
    const speedMetadata = useSelector(speedAndBearingSelector);
    const tachoMetadata = useSelector(tachoDataSelector);
    const userConfig = useSelector(userConfigSelector);
    const routePosition = useSelector(routePositionSelector);
    const secondarySessionData = useSelector(secondarySessionDataSelector);
    const dashboardID = userDetails.dashboardAccessID;
    const currentDashboard = _.find(dashboards, (dash) => dash.access_id === dashboardID);
    const userViewOffsets = _.get(views, [userDetails.userConfig.view_id, "datum_offsets"], []);
    const workspaceViewOffsets = _.get(views, [_.get(currentDashboard, ["config", "view_id"], -1), "datum_offsets"], []);

    const datumOffsets = workspaceViewOffsets.length ? workspaceViewOffsets : userViewOffsets;
    const [unitSelectionModalVisible, setUnitSelectionModalVisible] = useState(false);

    const handleArchiveInspectionSession = (status) => {
        dispatch(archiveInspectionSession(sessionID, status));
        session["inspection_archive_status"] = status;
    };

    useEffect(() => {
        if (sessionID) {
            dispatch(getDeviceGroupInfo(sessionID));
        }
    }, [dispatch, sessionID]);

    useEffect(() => {
        dispatch(getFavouriteCategories());
    }, [dispatch]);

    const updateHeaderHeight = useCallback(() => {
        setCurrentHeaderHeight(_.get(railInspectionHeader, ["current", "clientHeight"], 70)); // default to 70 (unwrapped header in case)
    }, [setCurrentHeaderHeight]);

    useEffect(() => {
        // add event listener on window resize
        window.addEventListener("resize", updateHeaderHeight);

        return () => {
            window.removeEventListener("resize", updateHeaderHeight);
        };
    }, [updateHeaderHeight]);

    const railImages = useMemo(() => {
        let retVal;
        if (railDataObject && railDataLoaded) {
            retVal = railDataObject; //Find first device in data - will need changing to support multiple devices
        } else {
            retVal = [];
        }
        return retVal;
    }, [railDataObject, railDataLoaded]);

    const railImage = useMemo(() => {
        if (railImages && railImages.length) {
            return railImages[selectedRailImageIndex];
        }
    }, [railImages, selectedRailImageIndex]);

    const positionCoords = useMemo(() => {
        let retVal = [];

        if (!_.isEmpty(currentCoordinates)) {
            return currentCoordinates;
        }

        return retVal;
    }, [currentCoordinates]);

    const calculatedELR = useMemo(() => {
        if (routePosition) {
            const calculatedELR = parseRoutePositionData(routePosition, routeSystemID);
            if (calculatedELR) {
                return {
                    string: calculatedELR.to_string(elrUnits, datumOffsets),
                    route: calculatedELR.route,
                    track: calculatedELR.track,
                    position: calculatedELR.position,
                };
            }
        }
    }, [routePosition, routeSystemID, elrUnits, datumOffsets]);

    const mwvData = useMemo(() => {
        if (railImages && railImage) {
            let mwv = railImage["mwv"];
            if (mwv && mwv.elr && mwv.mile && mwv.trid && mwv.yard) {
                mwv = ELRMileAndChain.from_fields(
                    "ELR Mile & Chain",
                    "elr_mile_yards",
                    {
                        ELR: mwv.elr,
                        MILE: mwv.mile,
                        TRACK: mwv.trid,
                        YARDS: mwv.yard,
                    },
                    datumOffsets,
                    true,
                );
                return {
                    string: mwv.to_string(elrUnits, datumOffsets, true),
                    route: mwv.route,
                    track: mwv.track,
                    position: mwv.position,
                };
            } else {
                return null;
            }
        } else {
            return null;
        }
    }, [railImage, railImages, datumOffsets, elrUnits]);

    const toggleSourceModal = useCallback(() => {
        setSourceModalOpen(!sourceModalOpen);
    }, [sourceModalOpen]);

    const goHome = useCallback(() => {
        dispatch(setDisplayExtraLayers(false));
        dispatch(toggleInspectionAnnotationMode(false));
        dispatch(targetResource(null));
        history.push("/", { sessionID });
        onExitRailInspection();
    }, [sessionID, history, dispatch, onExitRailInspection]);

    const exitButton = useMemo(() => {
        return (
            <>
                <button
                    onClick={goHome}
                    className="inspection-default-button close">
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </>
        );
    }, [goHome]);

    const distanceUnits = useMemo(() => {
        if (elrUnits === "elr_mile_chain") {
            return "ELR Mile & Chain";
        } else if (elrUnits === "elr_mile_yards") {
            return "ELR Mile & Yards";
        } else if (elrUnits === "elr_meterage") {
            return "ELR & Meterage";
        }
    }, [elrUnits]);

    const gotRailCoordinates = useCallback(
        (result) => {
            const l = [result[0].lon, result[0].lat];
            toggleFindModalVisibility(false);
            dispatch(routeSelected(sessionID, l));
        },
        [toggleFindModalVisibility, dispatch, sessionID],
    );

    useEffect(() => {
        if (foundRouteCoordinates && foundRouteCoordinates && foundRouteCoordinates.length) {
            gotRailCoordinates(foundRouteCoordinates);
        }
    }, [foundRouteCoordinates, gotRailCoordinates]);

    const formatPosition = (pos) => {
        let retVal = "";
        if (pos) {
            retVal = pos.toFixed(7);
        }
        return retVal;
    };

    const latLngPositionString = useMemo(() => {
        return formatPosition(positionCoords[1]) + ", " + formatPosition(positionCoords[0]);
    }, [positionCoords]);

    function LatLongCopier({ coords }) {
        return (
            <LazyTippy
                placement="bottom"
                sticky={true}
                plugins={[sticky]}
                arrow={true}
                interactive="true"
                content="Copy latitude and longitude to clipboard">
                <div
                    className="CopyToClipboard"
                    onClick={() => {
                        navigator.clipboard.writeText(coords);
                    }}>
                    <FontAwesomeIcon
                        icon={faClipboard}
                        color="white"
                        style={{ marginRight: 5, marginLeft: 5 }}
                    />
                </div>
            </LazyTippy>
        );
    }

    function ELRCopier({ distanceDetail }) {
        return (
            <LazyTippy
                placement="bottom"
                sticky={true}
                plugins={[sticky]}
                arrow={true}
                interactive="true"
                content="Copy ELR to clipboard">
                <div
                    className="CopyToClipboard"
                    onClick={() => {
                        navigator.clipboard.writeText(distanceDetail);
                    }}>
                    <FontAwesomeIcon
                        icon={faClipboard}
                        color="white"
                        style={{ marginRight: 5, marginLeft: 5 }}
                    />
                </div>
            </LazyTippy>
        );
    }

    const findMostRecent = memoize((metadata, timestamp) => {
        if (metadata) {
            return _.findLast(metadata, (data) => data.timestamp <= timestamp);
        } else {
            return null;
        }
    });

    const trainSpeed = useMemo(() => {
        let speed = "Unknown";
        let timestampSeconds = 0;

        if (!session) {
            return null;
        }

        if (videolessTimestamp) {
            timestampSeconds = videolessTimestamp / 1000;
            const tachoInfo = findMostRecent(tachoMetadata, timestampSeconds);
            let baseSpeed = null;
            let speedSource = "";
            if (!_.isNil(tachoInfo?.data.speed) && tachoInfo.timestamp > timestampSeconds - 5) {
                baseSpeed = tachoInfo?.data.speed;
                speedSource = " (Tachometer)";
            }

            const speedInfo = findMostRecent(speedMetadata, timestampSeconds);
            if (baseSpeed == null && !_.isNil(speedInfo?.data.speed) && speedInfo.timestamp > timestampSeconds - 5) {
                baseSpeed = speedInfo?.data.speed;
                speedSource = " (GPS)";
            }

            if (baseSpeed != null) {
                let unitDict = {
                    metres: "m/s",
                    miles: "MPH",
                    kilometers: "KPH",
                };
                let unit = userConfig.speed_units || "miles";
                speed = Math.round(convertSpeed(baseSpeed, unit)) + " " + unitDict[unit] + speedSource;
            }
        }

        return (
            <div className="inspectRail-Main__Header__Info">
                <MetadataFeed
                    items={["SPEED_AND_BEARING", "TACHO_DATA"]}
                    timestamp={timestampSeconds}>
                    <div className="source__Info">
                        <p>speed</p>
                        <span className="bar">{speed ? speed : "n/a"}</span>
                    </div>
                </MetadataFeed>
            </div>
        );
    }, [session, videolessTimestamp, findMostRecent, tachoMetadata, speedMetadata, userConfig.speed_units]);

    return (
        <>
            {unitSelectionModalVisible && <UnitSelectionModal onClose={() => setUnitSelectionModalVisible(false)} />}
            <div
                className="InspectionWindowBar relative"
                ref={railInspectionHeader}>
                <div
                    className="absolute InspectionLabel"
                    ref={(ref) => {
                        labelsRef.current["primary"] = ref;
                    }}>
                    <strong>{session && new Date(parseFloat(session.first_seen) * 1000).toDateString()}</strong>
                </div>

                <div
                    className="absolute InspectionLabel"
                    ref={(ref) => {
                        labelsRef.current["secondary"] = ref;
                    }}>
                    <strong>{secondarySessionData && new Date(parseFloat(secondarySessionData.first_seen) * 1000).toDateString()}</strong>
                </div>
                <img
                    src="/aivr-hybrid.svg"
                    alt="logo"
                    className="RailInspectHeaderLogo"
                />
                <div className="inspectRail-Main__Header__Row-Info">
                    <div className="inspectRail-Main__Header__Info">
                        <div className="source__Info">
                            <p>Workspace:</p>
                            <span>{currentDashboard && currentDashboard.description ? currentDashboard.description : ""}</span>
                        </div>
                    </div>

                    {session && session.route_name && session.route_name.length && (
                        <div className="inspectRail-Main__Header__Info">
                            <div className="source__Info">
                                <p>{session.route_name}</p>
                                <span>#{session.id}</span>
                            </div>
                            <div style={{ color: "white", marginRight: "5px", display: "flex", height: "100%", alignItems: "center", paddingTop: "2px" }}>
                                <FavoriteButton
                                    sessionID={sessionID}
                                    setFavouritePopoverOpen={setFavouritePopoverOpen}
                                />
                            </div>
                        </div>
                    )}
                    <div className="inspectRail-Main__Header__Info">
                        <div className="source__Info">
                            <p>LAT:</p>
                            <span>{formatPosition(positionCoords[1])}</span>
                            <p>LNG:</p>
                            <span>{formatPosition(positionCoords[0])}</span>
                        </div>
                        <div>
                            <LatLongCopier coords={latLngPositionString}></LatLongCopier>
                        </div>
                        <div className="source__Info elr">
                            <p>{distanceUnits}</p>
                            <FontAwesomeIcon
                                style={{ marginRight: 5, marginLeft: 5 }}
                                className="RouteInfo-ELRSettings__Icon"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setUnitSelectionModalVisible(true);
                                }}
                                icon={faCog}
                            />
                            {!mwvData && calculatedELR && calculatedELR.string && calculatedELR.string.length && (
                                <span
                                    className="bar"
                                    key={calculatedELR.string}>
                                    {calculatedELR.string}
                                </span>
                            )}
                            {mwvData && (
                                <span
                                    className="bar"
                                    key={mwvData.string}>
                                    {mwvData.string}
                                </span>
                            )}
                        </div>
                        <div>
                            <ELRCopier distanceDetail={!mwvData ? calculatedELR && calculatedELR.string : mwvData.string}></ELRCopier>
                        </div>
                    </div>
                    {trainSpeed}
                </div>
                {exitButton}
            </div>
            <RailInspectSourceModal
                toggleModal={toggleSourceModal}
                sourceModalOpen={sourceModalOpen}
            />
            <ArchiveInspectionSessionModal
                toggleModal={toggleArchiveInspectionSessionModal}
                archiveSessionModalVisible={archiveInspectionSessionModalVisible}
                sessionID={sessionID}
                archiveInspectionSession={handleArchiveInspectionSession}
            />

            <RestoreArchivedInspectionSessionModal
                toggleModal={toggleRestoreArchivedInspectionSessionModal}
                restoreArchivedSessionModalVisible={restoreArchivedInspectionSessionModalVisible}
                sessionID={sessionID}
                archiveInspectionSession={handleArchiveInspectionSession}
            />

            <FindLocationPopup
                visible={findModalVisible}
                onConfirm={() => toggleFindModalVisibility(false)}
                onCancel={() => toggleFindModalVisibility(false)}
                railInspection
            />
        </>
    );
};

export default RailInspectHeader;
